import React, { useState, useMemo } from 'react';
import { Star, Search, Trash2, Move, ExternalLink } from 'lucide-react';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { useAuth } from '../../hooks/useAuth';
import { getFavoritablePages, type FavoritablePage } from '../Navigation/MenuConfig';
import type { UserRoleValue } from '@shared/types/user';
import type { FavoriteItem } from '../../types/shell';
import { formatEasternDate } from '@/utils/dateFormatter';
import { useAuthStore } from '../../stores/authStore';
import { toRole } from '@shared/access/roles';

export const FavoritesManager: React.FC = () => {
  const { preferences, updatePreferences } = useUserPreferences();
  const { user } = useAuth();
  // Spec 285 — the viewing tenant's licenses: a page hidden by a license is not favoritable.
  const licenses = useAuthStore((s) => s.licenses);
  const [searchQuery, setSearchQuery] = useState('');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // The pages a user can actually add — flattened from the LIVE role-filtered menu, so
  // only real, reachable pages appear (the old hard-coded list was full of dead routes).
  // Spec 285: the role from useAuth is lowercase ('admin'); MenuConfig item roles are
  // PascalCase, so it is normalised through the shared parser (also makes SuperAdmin work).
  const availablePages = useMemo<FavoritablePage[]>(
    () => getFavoritablePages((toRole(user?.role) ?? 'Viewer') as UserRoleValue, licenses),
    [user?.role, licenses],
  );

  const isAlreadyFavorited = (path: string): boolean =>
    preferences.favorites.some((fav) => fav.path === path);

  // Filter the browsable page list by the search box (browse-all when empty).
  const filteredPages = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return availablePages;
    return availablePages.filter(
      (p) =>
        p.title.toLowerCase().includes(q) ||
        p.path.toLowerCase().includes(q) ||
        p.sectionLabel.toLowerCase().includes(q),
    );
  }, [availablePages, searchQuery]);

  // Star toggle = the "default marker" for selecting a favorite: click to add/remove.
  const toggleFavorite = (page: FavoritablePage): void => {
    if (isAlreadyFavorited(page.path)) {
      updatePreferences({ favorites: preferences.favorites.filter((f) => f.path !== page.path) });
      return;
    }
    const newFavorite: FavoriteItem = {
      id: crypto.randomUUID(),
      title: page.title,
      path: page.path,
      category: page.sectionLabel || 'Other',
      addedAt: new Date().toISOString(),
    };
    updatePreferences({ favorites: [...preferences.favorites, newFavorite] });
  };

  const removeFromFavorites = (favoriteId: string) => {
    const updatedFavorites = preferences.favorites.filter(fav => fav.id !== favoriteId);
    updatePreferences({ favorites: updatedFavorites });
  };

  const reorderFavorites = (fromIndex: number, toIndex: number) => {
    const updatedFavorites = [...preferences.favorites];
    const [movedItem] = updatedFavorites.splice(fromIndex, 1);
    updatedFavorites.splice(toIndex, 0, movedItem);
    updatePreferences({ favorites: updatedFavorites });
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    
    if (draggedIndex !== null && draggedIndex !== index) {
      reorderFavorites(draggedIndex, index);
      setDraggedIndex(index);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <div className="space-y-6">
      {/* Add New Favorites */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-lg font-medium text-[var(--color-text-primary)]">
            Add to Favorites
          </h3>
          <span className="text-sm text-[var(--color-text-secondary)]">
            {filteredPages.length} of {availablePages.length} pages
          </span>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-[var(--color-text-secondary)]" />
          <input
            type="text"
            placeholder="Filter pages by name, section, or path…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] py-2 pl-10 pr-4 text-[var(--color-text-primary)] placeholder:text-[var(--color-text-secondary)] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
          />
        </div>

        {/* Browsable, always-visible page list. Click the star to mark/unmark a favorite. */}
        <div className="mt-2 max-h-72 overflow-y-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] shadow-sm">
          {filteredPages.length === 0 ? (
            <p className="p-4 text-sm text-[var(--color-text-secondary)]">No pages match “{searchQuery}”.</p>
          ) : (
            filteredPages.map((page) => {
              const favorited = isAlreadyFavorited(page.path);
              return (
                <button
                  type="button"
                  key={page.path}
                  onClick={() => toggleFavorite(page)}
                  className="flex w-full items-center justify-between border-b border-[var(--color-border)] p-3 text-left last:border-b-0 hover:bg-[var(--color-bg-subtle)]"
                  title={favorited ? 'Remove from favorites' : 'Add to favorites'}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-[var(--color-text-primary)]">
                        {page.title}
                      </span>
                      <span className="flex-shrink-0 rounded bg-[var(--color-bg-subtle)] px-2 py-0.5 text-xs text-[var(--color-text-secondary)]">
                        {page.sectionLabel}
                      </span>
                    </div>
                    <div className="truncate text-xs text-[var(--color-text-secondary)]">{page.path}</div>
                  </div>
                  <Star
                    className={`ml-3 h-5 w-5 flex-shrink-0 transition-colors ${
                      favorited
                        ? 'fill-yellow-400 text-yellow-500'
                        : 'text-[var(--color-text-secondary)]'
                    }`}
                  />
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Current Favorites */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-medium text-[var(--color-text-primary)]">
            Your Favorites
          </h3>
          <span className="text-sm text-[var(--color-text-secondary)]">
            {preferences.favorites.length} items
          </span>
        </div>

        {preferences.favorites.length === 0 ? (
          <div className="text-center py-8">
            <Star className="h-12 w-12 text-[var(--color-text-secondary)] mx-auto mb-2" />
            <p className="text-[var(--color-text-secondary)] text-sm">
              No favorites yet. Search and add pages you visit frequently.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {preferences.favorites.map((favorite, index) => (
              <div
                key={favorite.id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`flex items-center space-x-3 p-3 bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg hover:shadow-sm transition-shadow cursor-move ${
                  draggedIndex === index ? 'opacity-50' : ''
                }`}
              >
                <Move className="h-4 w-4 text-[var(--color-text-secondary)] flex-shrink-0" />

                <Star className="h-4 w-4 text-yellow-500 flex-shrink-0" />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-[var(--color-text-primary)] truncate">
                      {favorite.title}
                    </span>
                    <span className="text-xs text-[var(--color-text-secondary)] bg-[var(--color-bg-subtle)] px-2 py-1 rounded flex-shrink-0">
                      {favorite.category}
                    </span>
                  </div>
                  <div className="text-xs text-[var(--color-text-secondary)] truncate">
                    {favorite.path}
                  </div>
                  <div className="text-xs text-[var(--color-text-secondary)]">
                    Added {formatEasternDate(favorite.addedAt)}
                  </div>
                </div>

                <div className="flex items-center space-x-1 flex-shrink-0">
                  <button
                    onClick={() => window.open(favorite.path, '_blank')}
                    className="p-1 text-[var(--color-text-secondary)] hover:text-[var(--color-accent)] transition-colors"
                    title="Open in new tab"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => removeFromFavorites(favorite.id)}
                    className="p-1 text-[var(--color-text-secondary)] hover:text-red-600 dark:hover:text-red-400 transition-colors"
                    title="Remove from favorites"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Favorites Tips */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
        <div className="flex">
          <Star className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
          <div className="ml-3">
            <h4 className="text-sm font-medium text-blue-900 dark:text-blue-200">
              Tips for Managing Favorites
            </h4>
            <div className="mt-1 text-xs text-blue-700 dark:text-blue-300 space-y-1">
              <p>• Drag and drop to reorder your favorites</p>
              <p>• Favorites appear at the top of your sidebar for quick access</p>
              <p>• Use search to find specific pages to add to your favorites</p>
              <p>• Remove favorites you no longer need to keep the list manageable</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FavoritesManager;